import { fmtTime } from './formatters.js'

export const ECG_FS = 130          // Hz — Polar H10 sampling rate

export function computeHRV(readings) {
  const raw = readings.flatMap(r => r.rr ?? []).filter(v => v > 250 && v < 2000)

  // Artifact rejection: discard intervals deviating >40% from median (catches missed/double beats)
  let rr = raw
  if (raw.length >= 3) {
    const sorted = [...raw].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    rr = raw.filter(v => Math.abs(v - median) / median < 0.4)
  }

  const mean  = rr.length ? rr.reduce((a, b) => a + b, 0) / rr.length : 0
  // SDNN: sample SD (N-1) per Task Force 1996 standard
  const sdnn  = rr.length >= 2
    ? Math.round(Math.sqrt(rr.reduce((a, b) => a + (b - mean) ** 2, 0) / (rr.length - 1)))
    : null

  let rmssd = null, pnn50 = null
  if (rr.length >= 2) {
    const diffs = rr.slice(1).map((v, i) => v - rr[i])
    rmssd = Math.round(Math.sqrt(diffs.reduce((a, b) => a + b * b, 0) / diffs.length))
    pnn50 = Math.round(diffs.filter(d => Math.abs(d) > 50).length / diffs.length * 100)
  }

  let respRate = null
  if (rr.length >= 10) {
    // Calculamos la frecuencia respiratoria usando ventanas móviles locales de 30 latidos
    // para filtrar la deriva de la línea de base y las fluctuaciones lentas de frecuencia cardíaca.
    const winSize = 30
    const rates = []
    for (let start = 0; start < rr.length - winSize; start += 15) { // 50% de solapamiento para suavizado
      const chunk = rr.slice(start, start + winSize)
      const chunkMean = chunk.reduce((a, b) => a + b, 0) / winSize
      const centered = chunk.map(v => v - chunkMean)
      let crossings = 0
      for (let i = 1; i < centered.length; i++) {
        if ((centered[i - 1] < 0) !== (centered[i] < 0)) crossings++
      }
      const chunkSec = chunk.reduce((a, b) => a + b, 0) / 1000
      if (chunkSec > 0) {
        const rawRate = (crossings / 2) / (chunkSec / 60)
        // Rango de respiración humana normal/fisiológica: entre 8 y 28 respiraciones por minuto
        if (rawRate >= 8 && rawRate <= 28) {
          rates.push(rawRate)
        }
      }
    }
    if (rates.length) {
      respRate = Math.round(rates.reduce((a, b) => a + b, 0) / rates.length)
    } else {
      respRate = null
    }
  }

  // Poincaré SD1 / SD2
  let sd1 = null, sd2 = null, sd1sd2 = null
  if (rr.length >= 10) {
    const diffs   = rr.slice(1).map((v, i) => v - rr[i])
    const sums    = rr.slice(1).map((v, i) => v + rr[i])
    const sumMean = sums.reduce((a, b) => a + b, 0) / sums.length
    sd1    = Math.round(Math.sqrt(diffs.reduce((a, b) => a + b * b, 0) / diffs.length) / Math.sqrt(2))
    sd2    = Math.round(Math.sqrt(sums.reduce((a, b) => a + (b - sumMean) ** 2, 0) / sums.length) / Math.sqrt(2))
    sd1sd2 = sd2 > 0 ? parseFloat((sd1 / sd2).toFixed(2)) : null
  }

  // DFA α1 (needs ≥ 30 RR)
  const dfa1 = rr.length >= 30 ? computeDFA(rr) : null

  // Sample Entropy (needs ≥ 50 RR, capped at 200)
  const sampen = rr.length >= 50 ? computeSampEn(rr) : null

  let tone = null, toneColor = 'var(--text-fainter)'
  if (rmssd !== null) {
    if (rmssd > 50)      { tone = 'Parasimpático alto'; toneColor = '#34c759' }
    else if (rmssd > 30) { tone = 'Equilibrado';         toneColor = '#007aff' }
    else if (rmssd > 15) { tone = 'Simpático activo';    toneColor = '#ff9f0a' }
    else                 { tone = 'Alta activación';     toneColor = '#ff3b30' }
  }

  return { rmssd, sdnn, pnn50, respRate, sd1, sd2, sd1sd2, dfa1, sampen, tone, toneColor, rrCount: rr.length }
}

export function computeHRDistribution(readings) {
  if (!readings.length) return []
  const buckets = {}
  readings.forEach(r => {
    if (!r.hr) return
    const b = Math.floor(r.hr / 5) * 5
    buckets[b] = (buckets[b] || 0) + 1
  })
  return Object.entries(buckets)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([bpm, count]) => ({
      label: String(bpm),
      bpmNum: Number(bpm),
      count,
      pct: Math.round(count / readings.length * 100),
    }))
}

export function computeHRVTrend(readings, windowMs = 120_000) {
  if (readings.length < 20) return []
  const stepMs = 60_000
  const start  = readings[0].time
  const end    = readings[readings.length - 1].time
  const points = []
  for (let t = start + windowMs; t <= end + stepMs; t += stepMs) {
    const win = readings.filter(r => r.time >= t - windowMs && r.time <= t)
    if (win.length < 10) continue
    const hrv = computeHRV(win)
    if (hrv.rmssd !== null && hrv.rrCount >= 5) {
      points.push({ time: Math.min(t, end), label: fmtTime(Math.min(t, end)), rmssd: hrv.rmssd, sdnn: hrv.sdnn ?? 0 })
    }
  }
  return points
}

export function computeArrhythmiaRisk(readings) {
  const raw = readings.flatMap(r => r.rr ?? []).filter(v => v > 250 && v < 2000)
  let rr = raw
  if (raw.length >= 3) {
    const sorted = [...raw].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    rr = raw.filter(v => Math.abs(v - median) / median < 0.4)
  }
  if (rr.length < 10) return null

  const mean = rr.reduce((a, b) => a + b, 0) / rr.length
  const sdnn = Math.sqrt(rr.reduce((a, b) => a + (b - mean) ** 2, 0) / (rr.length - 1))  // sample SD (N-1)
  const cv   = sdnn / mean

  const irregPct = Math.round(
    rr.slice(1).filter((v, i) => Math.abs(v - rr[i]) / mean > 0.2).length / (rr.length - 1) * 100
  )
  const longPauses     = rr.filter(v => v > 1500).length
  const shortIntervals = rr.filter(v => v < 300).length

  // Poincaré SD1/SD2
  const diffs   = rr.slice(1).map((v, i) => v - rr[i])
  const sd1     = Math.round(Math.sqrt(diffs.reduce((a, b) => a + b * b, 0) / diffs.length) / Math.sqrt(2))
  const sums    = rr.slice(1).map((v, i) => v + rr[i])
  const sumMean = sums.reduce((a, b) => a + b, 0) / sums.length
  const sd2     = Math.round(Math.sqrt(sums.reduce((a, b) => a + (b - sumMean) ** 2, 0) / sums.length) / Math.sqrt(2))
  const sd1sd2  = sd2 > 0 ? parseFloat((sd1 / sd2).toFixed(2)) : null

  const flags = []
  if (cv > 0.15)          flags.push({ label: 'Alta irregularidad', color: '#ff9f0a', detail: `CV = ${Math.round(cv * 100)}%` })
  if (irregPct > 40)      flags.push({ label: 'Cambios bruscos frecuentes', color: '#ff9f0a', detail: `${irregPct}% de pares con Δ>20%` })
  if (longPauses > 0)     flags.push({ label: 'Pausas largas (>1.5 s)', color: '#ff3b30', detail: `${longPauses} intervalos >1500 ms` })
  if (shortIntervals > 0) flags.push({ label: 'Intervalos muy cortos', color: '#ff3b30', detail: `${shortIntervals} intervalos <300 ms` })
  if (sd1sd2 !== null && sd1sd2 > 0.75) flags.push({ label: 'SD1/SD2 elevado', color: '#ff9f0a', detail: `Ratio = ${sd1sd2} (corto plazo domina)` })

  let risk, riskColor
  if (longPauses > 2 || shortIntervals > 5) { risk = 'Posible anomalía'; riskColor = '#ff3b30' }
  else if (flags.length >= 2)               { risk = 'Moderado';          riskColor = '#ff9f0a' }
  else if (flags.length === 1)              { risk = 'Leve';              riskColor = '#ffcc00' }
  else                                      { risk = 'Normal';            riskColor = '#34c759' }

  return { cv: Math.round(cv * 100), irregPct, longPauses, shortIntervals, sd1, sd2, sd1sd2, flags, risk, riskColor, rrCount: rr.length, meanRr: Math.round(mean), sdnn: Math.round(sdnn) }
}

// Cooley-Tukey radix-2 in-place FFT. Input length must be a power of 2.
// Returns power spectrum array (magnitude² / N), length N.
export function fftPower(signal) {
  const N    = signal.length
  const real = signal.slice()
  const imag = new Array(N).fill(0)
  // Bit-reversal
  let j = 0
  for (let i = 1; i < N; i++) {
    let bit = N >> 1
    for (; j & bit; bit >>= 1) j ^= bit
    j ^= bit
    if (i < j) { ;[real[i], real[j]] = [real[j], real[i]] }
  }
  // Butterfly
  for (let len = 2; len <= N; len <<= 1) {
    const ang = -2 * Math.PI / len
    const wr = Math.cos(ang), wi = Math.sin(ang)
    for (let i = 0; i < N; i += len) {
      let cr = 1, ci = 0
      for (let k = 0; k < len >> 1; k++) {
        const ur = real[i + k], ui = imag[i + k]
        const vr = real[i + k + (len >> 1)] * cr - imag[i + k + (len >> 1)] * ci
        const vi = real[i + k + (len >> 1)] * ci + imag[i + k + (len >> 1)] * cr
        real[i + k]            = ur + vr;  imag[i + k]            = ui + vi
        real[i + k + (len>>1)] = ur - vr;  imag[i + k + (len>>1)] = ui - vi
        ;[cr, ci] = [cr * wr - ci * wi, cr * wi + ci * wr]
      }
    }
  }
  return Array.from({ length: N }, (_, i) => (real[i] ** 2 + imag[i] ** 2) / N)
}

// Spectral HRV: VLF / LF / HF / LF:HF from RR intervals.
// Resamples to 4 Hz, applies Hann window, runs FFT, integrates power bands.
// Requires ≥ 60 RR intervals for meaningful results.
export function computeSpectralHRV(rr) {
  if (rr.length < 60) return null
  // Build cumulative time axis (seconds)
  const tRR = []
  let t = 0
  for (const r of rr) { t += r / 1000; tRR.push(t) }
  const tStart = tRR[0], tEnd = tRR[tRR.length - 1]
  const FS = 4
  const nRaw = Math.floor((tEnd - tStart) * FS)
  if (nRaw < 32) return null
  // Linear interpolation to uniform 4 Hz grid
  const resampled = new Array(nRaw)
  let ji = 0
  for (let i = 0; i < nRaw; i++) {
    const tq = tStart + i / FS
    while (ji < tRR.length - 2 && tRR[ji + 1] < tq) ji++
    const span = tRR[ji + 1] - tRR[ji]
    const frac = span > 0 ? (tq - tRR[ji]) / span : 0
    resampled[i] = rr[ji] + frac * (rr[ji + 1] - rr[ji])
  }
  // Detrend (subtract mean) + Hann window
  const mean = resampled.reduce((a, b) => a + b, 0) / nRaw
  const windowed = resampled.map((v, i) =>
    (v - mean) * (0.5 - 0.5 * Math.cos(2 * Math.PI * i / (nRaw - 1)))
  )
  // Zero-pad to next power of 2
  let N = 1; while (N < nRaw) N <<= 1
  const padded = new Array(N).fill(0)
  for (let i = 0; i < nRaw; i++) padded[i] = windowed[i]
  const power = fftPower(padded)
  const df = FS / N
  // Integrate bands
  let vlf = 0, lf = 0, hf = 0, tot = 0
  const specPoints = []
  for (let i = 1; i < N >> 1; i++) {
    const freq = i * df
    const p    = power[i]
    if (freq >= 0.003 && freq < 0.04)  vlf += p
    if (freq >= 0.04  && freq < 0.15)  { lf  += p }
    if (freq >= 0.15  && freq < 0.40)  hf  += p
    if (freq >= 0.003 && freq < 0.40)  tot += p
    if (freq <= 0.50)
      specPoints.push({ freq: parseFloat(freq.toFixed(3)), power: p })
  }
  const lfNu  = tot > 0 ? Math.round(lf / tot * 100) : 0
  const hfNu  = tot > 0 ? Math.round(hf / tot * 100) : 0
  const lfHf  = hf > 0  ? parseFloat((lf / hf).toFixed(2)) : null
  return { vlf: Math.round(vlf), lf: Math.round(lf), hf: Math.round(hf),
           lfNu, hfNu, lfHf, specPoints, rrCount: rr.length }
}

// DFA α1: short-term fractal scaling exponent (box sizes n = 4–16).
// α1 < 0.75 → loss of fractal structure (overtraining/illness)
// α1 0.75–1.0 → healthy nonlinear complexity
// α1 > 1.0   → pathological rigidity or very low HR
// Requires ≥ 30 RR intervals.
export function computeDFA(rr) {
  if (rr.length < 30) return null
  const N    = rr.length
  const mean = rr.reduce((a, b) => a + b, 0) / N
  // Integrate: cumulative sum of mean-subtracted series
  const y = new Array(N)
  y[0] = rr[0] - mean
  for (let i = 1; i < N; i++) y[i] = y[i - 1] + (rr[i] - mean)
  // Compute F(n) for short-term box sizes
  const logN = [], logF = []
  const nMax = Math.min(16, Math.floor(N / 4))
  for (let n = 4; n <= nMax; n++) {
    const numBoxes = Math.floor(N / n)
    let sumSq = 0
    for (let b = 0; b < numBoxes; b++) {
      const s = b * n, e = s + n
      // Linear detrend this box via least-squares
      let sx = 0, sy = 0, sxy = 0, sxx = 0
      for (let i = s; i < e; i++) { sx += i; sy += y[i]; sxy += i * y[i]; sxx += i * i }
      const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1)
      const icept = (sy - slope * sx) / n
      for (let i = s; i < e; i++) sumSq += (y[i] - (slope * i + icept)) ** 2
    }
    const Fn = Math.sqrt(sumSq / (numBoxes * n))
    if (Fn > 0) { logN.push(Math.log10(n)); logF.push(Math.log10(Fn)) }
  }
  if (logN.length < 3) return null
  // OLS slope = α1
  const nMean = logN.reduce((a, b) => a + b, 0) / logN.length
  const fMean = logF.reduce((a, b) => a + b, 0) / logF.length
  let num = 0, den = 0
  for (let i = 0; i < logN.length; i++) {
    num += (logN[i] - nMean) * (logF[i] - fMean)
    den += (logN[i] - nMean) ** 2
  }
  return den > 0 ? parseFloat((num / den).toFixed(3)) : null
}

// Sample Entropy: lower = more regular/stressed, higher = more complex/healthy.
// m=2, r=0.2×SD, capped at 200 intervals for performance (O(n²)).
export function computeSampEn(rr) {
  const data = rr.length > 200 ? rr.slice(-200) : rr
  if (data.length < 50) return null
  const N    = data.length
  const mean = data.reduce((a, b) => a + b, 0) / N
  const sd   = Math.sqrt(data.reduce((a, b) => a + (b - mean) ** 2, 0) / N)
  const r    = 0.2 * sd
  if (r <= 0) return null
  const m = 2
  function countB(len) {
    let c = 0
    for (let i = 0; i < N - len; i++) {
      for (let j = i + 1; j < N - len; j++) {
        let ok = true
        for (let k = 0; k < len; k++) {
          if (Math.abs(data[i + k] - data[j + k]) > r) { ok = false; break }
        }
        if (ok) c++
      }
    }
    return c
  }
  const A = countB(m + 1), B = countB(m)
  if (B === 0 || A === 0) return null
  return parseFloat((-Math.log(A / B)).toFixed(3))
}

// IIR high-pass filter — removes baseline wander (fc ≈ 0.5 Hz at 130 Hz)
// y[n] = α·(y[n-1] + x[n] - x[n-1])   α = 0.9762
export function hpFilter(samples) {
  if (samples.length < 2) return samples.slice()
  const α = 0.9762
  const out = new Array(samples.length)
  out[0] = 0
  for (let i = 1; i < samples.length; i++) {
    out[i] = α * (out[i - 1] + samples[i] - samples[i - 1])
  }
  return out
}

// IIR notch filter at 50 Hz (Argentina/EU power line frequency) — 2nd-order
// Uses the standard biquad notch: wo = 2π·f0/fs, BW = wo/Q (Q=30 for narrow notch)
export function notchFilter50(samples) {
  if (samples.length < 3) return samples.slice()
  const fs = ECG_FS
  const f0 = 50
  const Q  = 30
  const wo = 2 * Math.PI * f0 / fs
  const cosWo = Math.cos(wo)
  const alpha  = Math.sin(wo) / (2 * Q)
  // biquad coefficients
  const b0 =  1,  b1 = -2 * cosWo, b2 = 1
  const a0 =  1 + alpha
  const a1 = -2 * cosWo
  const a2 =  1 - alpha
  const out = new Array(samples.length)
  out[0] = samples[0]; out[1] = samples[1]
  for (let i = 2; i < samples.length; i++) {
    out[i] = (b0 * samples[i] + b1 * samples[i-1] + b2 * samples[i-2]
              - a1 * out[i-1] - a2 * out[i-2]) / a0
  }
  return out
}

// Pan-Tompkins-lite R-peak detector for ECG annotation
// Returns array of sample indices where R-peaks occur
export function detectRPeaks(filtered, fs) {
  const n = filtered.length
  if (n < fs * 0.5) return []   // need at least 0.5 s
  // 1) Derivative
  const diff = new Array(n).fill(0)
  for (let i = 2; i < n - 2; i++) diff[i] = (-filtered[i-2] - 2*filtered[i-1] + 2*filtered[i+1] + filtered[i+2]) / 8
  // 2) Square
  const sq = diff.map(v => v * v)
  // 3) Moving-window integration (window ≈ 150 ms)
  const winN = Math.round(0.15 * fs)
  const mwi  = new Array(n).fill(0)
  let   sum  = 0
  for (let i = 0; i < n; i++) {
    sum += sq[i]
    if (i >= winN) sum -= sq[i - winN]
    mwi[i] = sum / winN
  }
  // 4) Adaptive threshold (60% of running max)
  const peaks = []
  const minDist = Math.round(0.25 * fs)   // min 250 ms between beats
  let   threshold = mwi.slice(0, Math.min(n, fs)).reduce((a, b) => Math.max(a, b), 0) * 0.6
  let   lastPeak  = -minDist

  for (let i = winN; i < n - winN; i++) {
    if (mwi[i] > threshold && mwi[i] >= mwi[i-1] && mwi[i] >= mwi[i+1] && i - lastPeak >= minDist) {
      // Find the true maximum of the original signal in a ±10-sample window
      let maxIdx = i, maxVal = filtered[i]
      for (let j = Math.max(0, i-10); j <= Math.min(n-1, i+10); j++) {
        if (filtered[j] > maxVal) { maxVal = filtered[j]; maxIdx = j }
      }
      peaks.push(maxIdx)
      lastPeak  = maxIdx
      threshold = 0.75 * threshold + 0.25 * mwi[i]  // adapt
    }
  }
  return peaks
}

// Estado interno para filtrado temporal y desacople de re-renders
let _lastComputeTs = 0
let _cachedResult = null
let _moistureHistory = []

/**
 * Índice de Ajuste de Banda, Proximidad y Calidad de Contacto (0 - 100%).
 * 
 * Evalúa dos factores fisiológicos esenciales del Polar H10 a 130 Hz:
 *  1. Proximidad y alineación cardíaca (Voltaje QRS en µV/mV):
 *     Mide la amplitud pico a pico de los latidos. Cuanto más cerca y centrado
 *     esté el sensor del esternón/corazón, mayor es el voltaje (800 - 2500 µV).
 *     Si la banda está muy baja, en la espalda o lejos, el voltaje cae (< 400 µV).
 *  2. Calidad de contacto e impedancia de la piel (MAD de 50 Hz):
 *     Mide el ruido de alta frecuencia entre electrodos. Si la piel está húmeda
 *     y firme, el ruido es mínimo (< 9 µV). Si está seca o floja, sube (> 30 µV).
 */
export function computeMoistureIndex(ecgSamples, skinContact = true, hr = null) {
  const now = Date.now()
  // Si transcurrieron menos de 850 ms desde la última evaluación, devolver caché
  if (_cachedResult && now - _lastComputeTs < 850) {
    return _cachedResult
  }
  _lastComputeTs = now

  if (skinContact === false) {
    _moistureHistory.push(10)
    if (_moistureHistory.length > 6) _moistureHistory.shift()
    const avg = Math.round(_moistureHistory.reduce((a, b) => a + b, 0) / _moistureHistory.length)
    _cachedResult = { pct: avg, label: 'Sin contacto', status: 'dry', color: '#ff453a' }
    return _cachedResult
  }

  const buf = Array.isArray(ecgSamples) ? ecgSamples : (ecgSamples?.current || [])
  if (buf.length < 130) {
    const fallback = hr ? 85 : 50
    _cachedResult = { pct: fallback, label: 'Detectado', status: 'good', color: '#30d158' }
    return _cachedResult
  }

  // Tomamos los últimos 260 puntos (2 segundos de señal a 130 Hz)
  const slice = buf.slice(-260)
  const n = slice.length

  // 1. Amplitud pico a pico (Voltaje del complejo QRS en µV)
  let min = slice[0], max = slice[0]
  for (let i = 0; i < n; i++) {
    const v = slice[i]
    if (v < min) min = v
    if (v > max) max = v
  }
  const peakToPeak = max - min
  if (peakToPeak < 30) {
    _moistureHistory.push(10)
    if (_moistureHistory.length > 6) _moistureHistory.shift()
    const avg = Math.round(_moistureHistory.reduce((a, b) => a + b, 0) / _moistureHistory.length)
    _cachedResult = { pct: avg, label: 'Sin señal', status: 'dry', color: '#ff453a' }
    return _cachedResult
  }

  // Puntuación de proximidad al corazón según voltaje pico (0.8 mV a 2.5 mV es óptimo):
  let voltScore = 80
  if (peakToPeak >= 1100) {
    voltScore = 100 // Cerca del corazón y centrado en el esternón
  } else if (peakToPeak >= 750) {
    voltScore = 85 + ((peakToPeak - 750) / 350) * 15
  } else if (peakToPeak >= 400) {
    voltScore = 60 + ((peakToPeak - 400) / 350) * 25
  } else if (peakToPeak >= 180) {
    voltScore = 35 + ((peakToPeak - 180) / 220) * 25 // Lejos o ladeado
  } else {
    voltScore = Math.max(10, Math.round((peakToPeak / 180) * 35))
  }

  // 2. Mediana de diferencias consecutivas (MAD) para medir ruido de impedancia
  const diffs = []
  for (let i = 1; i < n; i++) {
    diffs.push(Math.abs(slice[i] - slice[i - 1]))
  }
  diffs.sort((a, b) => a - b)
  const medianDiff = diffs[Math.floor(diffs.length / 2)] || 10

  let impScore = 85
  if (medianDiff <= 9) {
    impScore = 100 // Contacto húmedo y adherente ideal
  } else if (medianDiff <= 20) {
    impScore = 80 + ((20 - medianDiff) / 11) * 20
  } else if (medianDiff <= 45) {
    impScore = 50 + ((45 - medianDiff) / 25) * 30
  } else {
    impScore = Math.max(15, Math.round(50 - (medianDiff - 45) * 0.7))
  }

  // Combinación ponderada: 55% voltaje/proximidad al corazón + 45% impedancia/firmeza
  const rawPct = Math.round(voltScore * 0.55 + impScore * 0.45)

  // Búfer móvil de los últimos 6 segundos para máxima estabilidad
  _moistureHistory.push(rawPct)
  if (_moistureHistory.length > 6) {
    _moistureHistory.shift()
  }

  const smoothPct = Math.round(_moistureHistory.reduce((a, b) => a + b, 0) / _moistureHistory.length)
  const pct = Math.max(10, Math.min(100, smoothPct))

  let label, status, color
  if (pct >= 80) {
    label = 'Excelente'; status = 'optimal'; color = '#30d158'
  } else if (pct >= 60) {
    label = 'Bueno'; status = 'good'; color = '#34c759'
  } else if (pct >= 40) {
    label = 'Lejos/Flojo'; status = 'medium'; color = '#ff9f0a'
  } else {
    label = 'Ajustar banda'; status = 'dry'; color = '#ff453a'
  }

  _cachedResult = { pct, label, status, color, mv: parseFloat((peakToPeak / 1000).toFixed(2)) }
  return _cachedResult
}

export const TRAINING_SCENARIOS = [
  {
    id: 1,
    name: 'Cuerpo Óptimo / Máxima Energía',
    subtitle: 'Sistema nervioso 100% recuperado y reservas altas',
    stateBadge: 'Apto para entrenar fuerte',
    stateColor: '#30d158',
    why: 'Tu freno parasimpático (nervio vago) está plenamente activo y el corazón tiene excelente elasticidad. El cuerpo tolera estrés metabólico y neuromuscular alto.',
    cardio: {
      verdict: 'Ideal',
      badgeColor: '#30d158',
      summary: 'Cardio de alta intensidad (HIIT, pasadas o fondos rápidos)',
      detail: 'Tu sistema cardiovascular tiene máxima reserva. Podés hacer series de velocidad, cuestas o intervalos al 90-95% de FC sin riesgo de sobrecarga.'
    },
    fuerza: {
      verdict: 'Ideal',
      badgeColor: '#30d158',
      summary: 'Fuerza pesada o récords personales (PR)',
      detail: 'El sistema neuromuscular recluta fibras con máxima eficiencia. Excelente día para mover kilos pesados (RPE 8-9) o entrenar grupos musculares grandes (pierna/espalda).'
    },
    avoid: 'Solo asegurate de calentar bien articulaciones antes de levantar cargas máximas.'
  },
  {
    id: 2,
    name: 'Tensión Mental o Estrés Alto, Músculos Frescos',
    subtitle: 'Sistema simpático acelerado, pero sin fatiga muscular profunda',
    stateBadge: 'Entrenamiento selectivo: Mejor pesas que cardio',
    stateColor: '#ff9f0a',
    why: 'Tenés el sistema de alerta encendido (cortisol y adrenalina por trabajo, preocupaciones o cafeína), pero tus fibras musculares no están agotadas.',
    cardio: {
      verdict: 'No recomendado',
      badgeColor: '#ff9f0a',
      summary: 'Evitar cardio largo o extenuante',
      detail: 'El running de más de 35 minutos o el spinning intenso disparan aún más el cortisol, profundizan la fatiga mental y te van a dejar agotado para el día siguiente.'
    },
    fuerza: {
      verdict: 'Muy recomendado (Mejor opción)',
      badgeColor: '#30d158',
      summary: 'Fuerza hipertrofia con pausas largas (2–3 min)',
      detail: 'Levantar pesas a intensidad moderada (series de 8 a 12 repeticiones) descarga la adrenalina y la tensión acumulada sin agotar el glucógeno. Descansar 2 a 3 minutos entre series calma el sistema nervioso.'
    },
    avoid: 'Evitá correr más de 35 minutos, no hagas HIIT y no tomes cafeína ni estimulantes extra.'
  },
  {
    id: 3,
    name: 'Recuperación Estable / Día de Mantenimiento',
    subtitle: 'Cuerpo equilibrado para sumar volumen de calidad',
    stateBadge: 'Apto para entrenamiento estándar',
    stateColor: '#007aff',
    why: 'Tu organismo está en balance normal. No estás para quebrar tus récords de fuerza, pero tenés capacidad plena para una sesión productiva.',
    cardio: {
      verdict: 'Recomendado',
      badgeColor: '#007aff',
      summary: 'Cardio continuo en Zona 2 (Aeróbico base)',
      detail: 'Trote suave, bici o elíptica a ritmo conversacional (pudiendo hablar sin ahogarte durante 45–60 min). Multiplica mitocondrias sin desgastar articulaciones.'
    },
    fuerza: {
      verdict: 'Recomendado',
      badgeColor: '#007aff',
      summary: 'Fuerza estándar sin llegar al fallo (RIR 2–3)',
      detail: 'Rutina clásica de hipertrofia o técnica. Quedate a 2 o 3 repeticiones antes del fallo muscular para garantizar una recuperación rápida.'
    },
    avoid: 'Evitá buscar 1RM (repetición máxima) o llegar al fallo extremo en sentadillas o peso muerto.'
  },
  {
    id: 4,
    name: 'Cansancio Acumulado / Batería Baja',
    subtitle: 'Fatiga de días previos, entrenamiento duro o poco sueño',
    stateBadge: 'Descanso sugerido / Sesión muy suave',
    stateColor: '#ff9f0a',
    why: 'El tono parasimpático está debilitado y tu corazón late un poco más rápido en reposo para compensar la falta de recuperación celular.',
    cardio: {
      verdict: 'Solo regenerativo',
      badgeColor: '#ff9f0a',
      summary: 'Caminata enérgica o Zona 1 muy suave',
      detail: 'Caminata de 30 a 40 minutos al aire libre o pedaleo muy liviano. El único objetivo es bombear sangre para limpiar metabolitos y desinflamar piernas, jamás cansarte.'
    },
    fuerza: {
      verdict: 'Desaconsejado',
      badgeColor: '#ff453a',
      summary: 'No hacer fuerza pesada',
      detail: 'Tus tendones y tu sistema nervioso no tienen velocidad de respuesta. Levantar pesado con esta fatiga dispara el riesgo de contracturas, lumbalgias o desgarros.'
    },
    avoid: 'Prohibido: series pesadas al fallo, crossfit, piques de velocidad o partidos intensos de fútbol/pádel.'
  },
  {
    id: 5,
    name: 'Alerta de Sobrecarga / Agotamiento Extremo',
    subtitle: 'Freno parasimpático colapsado o inicio de cuadro viral',
    stateBadge: 'Descanso obligatorio',
    stateColor: '#ff453a',
    why: 'Tu cuerpo está pidiendo auxilio fisiológico. La variabilidad cardíaca se desplomó. Forzar un entrenamiento en este estado debilita el sistema inmune y destruye músculo.',
    cardio: {
      verdict: 'Prohibido',
      badgeColor: '#ff453a',
      summary: 'Cero cardio hoy',
      detail: 'Cualquier exigencia aeróbica hoy cataboliza masa muscular y prolonga la inflamación por varios días.'
    },
    fuerza: {
      verdict: 'Prohibido',
      badgeColor: '#ff453a',
      summary: 'Cero pesas hoy',
      detail: 'Tu cuerpo no tiene reserva biológica para reparar el daño tisular de las pesas.'
    },
    avoid: 'Prioridad absoluta: Dormir 8 horas, buena hidratación, comida nutritiva y respiración profunda.'
  }
]

/**
 * Evalúa Cansancio, Estrés y Recomendación de Entrenamiento (Cardio vs Fuerza).
 * 
 * Basado en:
 *  - RMSSD (Tono vagal parasimpático / recuperación)
 *  - Frecuencia cardíaca mínima y promedio en reposo vs basal
 *  - Carga alostática del sistema nervioso autónomo
 */
export function computeReadinessAndWorkoutAdvice(rmssd, currentHr, minHr, avgHr) {
  // Baselines fisiológicos de referencia (joven sano 24 años):
  // RMSSD normal en reposo: 45 - 75 ms
  // FC reposo normal: 58 - 68 bpm
  const rmssdBase = 52
  const hrRestBase = 62

  const safeRmssd = (rmssd != null && rmssd > 0) ? rmssd : 48
  const evalHr = minHr || currentHr || 65

  // 1. Índice de Recuperación / Energía (0 - 100%): más alto = más descansado
  const rmssdRatio = safeRmssd / rmssdBase
  let recScore = Math.round(rmssdRatio * 72)
  if (evalHr > hrRestBase) {
    recScore -= Math.round((evalHr - hrRestBase) * 1.3)
  }
  const recoveryScore = Math.min(100, Math.max(10, recScore))

  // 2. Índice de Cansancio / Fatiga (0 - 100%)
  const fatigueScore = Math.max(0, 100 - recoveryScore)

  // 3. Índice de Estrés Fisiológico (0 - 100%): predominio del tono simpático
  const hrvStress = Math.min(95, Math.max(5, Math.round(50 - ((safeRmssd - rmssdBase) / rmssdBase) * 45)))
  const hrStress = Math.min(95, Math.max(5, Math.round(50 + ((evalHr - hrRestBase) / hrRestBase) * 45)))
  const stressScore = Math.min(100, Math.max(5, Math.round(hrvStress * 0.65 + hrStress * 0.35)))

  // 4. Identificar el escenario clínico correspondiente
  let activeScenario = TRAINING_SCENARIOS[2] // Escenario 3 por defecto
  if (recoveryScore < 32 || fatigueScore > 68) {
    activeScenario = TRAINING_SCENARIOS[4] // Escenario 5: Agotamiento
  } else if (recoveryScore < 50 || fatigueScore > 50) {
    activeScenario = TRAINING_SCENARIOS[3] // Escenario 4: Cansancio acumulado
  } else if (stressScore > 55 && recoveryScore < 75) {
    activeScenario = TRAINING_SCENARIOS[1] // Escenario 2: Estrés alto, músculos frescos
  } else if (recoveryScore >= 75 && stressScore <= 45) {
    activeScenario = TRAINING_SCENARIOS[0] // Escenario 1: Óptimo
  } else {
    activeScenario = TRAINING_SCENARIOS[2] // Escenario 3: Mantenimiento
  }

  let recoveryColor = activeScenario.stateColor
  let stressColor = stressScore > 68 ? '#ff453a' : stressScore > 42 ? '#ff9f0a' : '#30d158'
  let fatigueColor = fatigueScore > 60 ? '#ff453a' : fatigueScore > 35 ? '#ff9f0a' : '#30d158'

  return {
    recoveryScore,
    fatigueScore,
    stressScore,
    status: activeScenario.id <= 3 ? 'ready' : 'rest',
    shouldTrain: activeScenario.id <= 3,
    scenario: activeScenario,
    trainAdvice: activeScenario.subtitle,
    modalityTitle: activeScenario.cardio.verdict === 'Ideal' ? 'Fuerza pesada o Cardio intenso' : activeScenario.fuerza.badgeColor === '#30d158' ? 'Mejor FUERZA que cardio' : activeScenario.cardio.summary,
    modalityAdvice: activeScenario.why,
    recoveryColor,
    stressColor,
    fatigueColor,
  }
}
